import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form } from 'react-bootstrap';
import { FaMapMarkerAlt, FaFilter } from 'react-icons/fa';
import { projectsAPI } from '../utils/api';
import Loader from '../components/common/Loader';
import './Projects.css';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState(['All']);

  useEffect(() => {
    document.title = 'Our Projects | Infrastructure Company';
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getAll();
      const projectsData = response.data.data || [];
      setProjects(projectsData);
      setFilteredProjects(projectsData);

      // Extract unique categories
      const uniqueCategories = ['All', ...new Set(projectsData.map(p => p.category))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
      setFilteredProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category === 'All') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(p => p.category === category));
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="projects-page">
      {/* Page Header */}
      <section className="page-header">
        <Container>
          <div className="text-center">
            <h1 className="display-4 fw-bold mb-3">Our Projects</h1>
            <p className="lead">
              Showcasing Excellence in Infrastructure Development Across India
            </p>
          </div>
        </Container>
      </section>

      {/* Filter Section */}
      <section className="filter-section">
        <Container>
          <Card className="filter-card">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div className="d-flex align-items-center gap-2">
                  <FaFilter className="text-primary" />
                  <h5 className="mb-0">Filter by Category:</h5>
                </div>
                <div className="filter-buttons d-flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <Button
                      key={category}
                      variant={selectedCategory === category ? 'primary' : 'outline-primary'}
                      size="sm"
                      onClick={() => handleCategoryChange(category)}
                      className="filter-btn"
                    >
                      {category}
                    </Button>
                  ))}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </section>

      {/* Projects Grid */}
      <section className="section">
        <Container>
          {filteredProjects.length > 0 ? (
            <>
              <div className="text-center mb-4">
                <p className="text-muted">
                  Showing {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
                  {selectedCategory !== 'All' && ` in ${selectedCategory}`}
                </p>
              </div>

              <Row className="g-4">
                {filteredProjects.map((project) => (
                  <Col key={project._id} md={6} lg={4}>
                    <Link to={`/projects/${project.slug}`} className="text-decoration-none">
                      <Card className="project-card-item h-100">
                        <div className="project-image-container">
                          <img
                            src={project.thumbnail}
                            alt={project.title}
                            className="card-img-top project-img"
                          />
                          <div className="project-overlay-gradient"></div>
                          <span className="project-badge">{project.category}</span>
                          {project.status && (
                            <span className={`status-badge status-${project.status.toLowerCase()}`}>
                              {project.status}
                            </span>
                          )}
                        </div>
                        <Card.Body>
                          <h3 className="project-card-title">{project.title}</h3>
                          <p className="text-muted mb-3">{project.shortDescription || project.description?.substring(0, 100) + '...'}</p>
                          
                          <div className="project-meta">
                            <div className="meta-item">
                              <FaMapMarkerAlt className="meta-icon" />
                              <span>{project.location?.city}, {project.location?.state}</span>
                            </div>
                          </div>

                          <div className="d-flex justify-content-between align-items-center mt-3">
                            <span className="text-primary fw-semibold">View Details →</span>
                          </div>
                        </Card.Body>
                      </Card>
                    </Link>
                  </Col>
                ))}
              </Row>
            </>
          ) : (
            <div className="text-center py-5">
              <h3 className="text-muted">No projects found</h3>
              <p className="text-muted">Try selecting a different category</p>
              <Button variant="primary" onClick={() => handleCategoryChange('All')}>
                Show All Projects
              </Button>
            </div>
          )}
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <Container>
          <div className="text-center text-white">
            <h2 className="fw-bold mb-4">Have a Project in Mind?</h2>
            <p className="lead mb-4">
              Let's discuss how we can help you achieve your infrastructure goals
            </p>
            <Button as={Link} to="/contact" size="lg" className="btn-white">
              Start a Project
            </Button>
          </div>
        </Container>
      </section>

      <div className="indian-accent"></div>
    </div>
  );
};

export default Projects;